import type {
  BookingItemInspectionSessionListApiData,
  BookingItemInspectionSessionListApiResponses,
  BookingItemInspectionSessionSkipApiData,
  BookingItemInspectionSessionSkipApiResponses,
  BookingSlugInspectionSessionListApiData,
  BookingSlugInspectionSessionListApiResponses,
  CarInspectionSessionListApiData,
  CarInspectionSessionListApiResponses,
  CarWheelSetInspectionsLatestApiData,
  CarWheelSetInspectionsLatestApiResponses,
  InspectionItemCreateApiData,
  InspectionItemCreateApiResponses,
  InspectionItemPatchApiData,
  InspectionItemPatchApiResponses,
  InspectionSessionCreateApiData,
  InspectionSessionCreateApiResponses,
  InspectionSessionGetApiData,
  InspectionSessionGetApiResponses,
  InspectionSessionListApiData,
  InspectionSessionListApiResponses,
  InspectionSessionVoidApiData,
  InspectionSessionVoidApiResponses
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import {
  toDimensionBody,
  toImageInput,
  toInspectionSessionBody,
  type InspectionImageInput,
  type LegacyDimensionInput,
  type LegacyInspectionImageInput,
  type LegacyTireMeasurementsInput
} from './legacyWorkerInspection';

export const getInspectionUrls = {
  postTireMeasurements: responseEndpoint<
    InspectionSessionCreateApiData,
    InspectionSessionCreateApiResponses,
    ApiInput<InspectionSessionCreateApiData> | LegacyTireMeasurementsInput
  >({
    getUrl: () => apiUrl('inspection-sessions/'),
    getBody: (data) => toInspectionSessionBody(data)
  }),
  postUnableToMeasureTires: responseEndpoint<
    BookingItemInspectionSessionSkipApiData,
    BookingItemInspectionSessionSkipApiResponses
  >({
    getUrl: ({ bookingItemId }) => apiUrl(`booking-items/${bookingItemId}/inspection-sessions/skip/`),
    getBody: (data) => data
  }),
  postCarInspectionImage: responseEndpoint<
    InspectionItemCreateApiData,
    InspectionItemCreateApiResponses,
    InspectionImageInput | LegacyInspectionImageInput
  >({
    getUrl: () => apiUrl('inspection-items/'),
    // `skipBodyTransformation` turns off the shared camelCase -> snake_case pass, which only
    // handles JSON. Multipart parts are named here instead; keep the two spellings in step.
    getBody: (input) => {
      const { imageData, ...rest } = toImageInput(input);
      const formData = new FormData();
      formData.append('car_id', String(rest.carId));
      formData.append('kind', rest.kind ?? 'PHOTO');
      formData.append('location', String(rest.location));
      formData.append('context', String(rest.context ?? 'STANDALONE'));
      // Multipart cannot nest objects; nsdk flattens tire.season / tire.specification.
      if (rest.tireSeason) {
        formData.append('tire_season', String(rest.tireSeason));
      }
      if (rest.tireSpecification) {
        formData.append('tire_specification', String(rest.tireSpecification));
      }
      if (rest.bookingItemId) {
        formData.append('booking_item_id', String(rest.bookingItemId));
      }
      if (imageData instanceof File) {
        formData.append('image', imageData, imageData.name);
      } else {
        //@ts-expect-error React Native image shape for multipart
        formData.append('image', imageData);
      }
      return formData;
    },
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    skipBodyTransformation: true
  }),
  postCarInspectionDimensions: responseEndpoint<
    InspectionItemCreateApiData,
    InspectionItemCreateApiResponses,
    ApiInput<InspectionItemCreateApiData> | LegacyDimensionInput
  >({
    getUrl: () => apiUrl('inspection-items/'),
    getBody: (data) => {
      const input = toDimensionBody(data);
      return {
        carId: input.carId,
        bookingItemId: input.bookingItemId,
        // A photo item requires an image. Dimension-only observations are SPEC.
        kind: input.kind ?? 'SPEC',
        location: input.location,
        context: input.context ?? 'STANDALONE',
        tireSeason: input.tireSeason,
        tireSpecification: input.tireSpecification
      };
    }
  }),
  getLatestCarWheelInspections: dataEndpoint<
    CarWheelSetInspectionsLatestApiData,
    CarWheelSetInspectionsLatestApiResponses
  >({
    getUrl: (queryParams) => apiUrl('car-wheel-set-inspections/latest/', queryParams)
  }),
  getCarInspectionSessions: dataEndpoint<CarInspectionSessionListApiData, CarInspectionSessionListApiResponses>({
    getUrl: ({ carId, ...queryParams }) => apiUrl(`cars/${carId}/inspection-sessions/`, queryParams)
  }),
  getBookingItemInspectionSessions: dataEndpoint<
    BookingItemInspectionSessionListApiData,
    BookingItemInspectionSessionListApiResponses
  >({
    getUrl: ({ bookingItemId }) => apiUrl(`booking-items/${bookingItemId}/inspection-sessions/`)
  }),
  // Public: the slug from the customer's SMS link is the access key, so the booking page works logged out.
  getBookingInspectionSessionsFromSlug: dataEndpoint<
    BookingSlugInspectionSessionListApiData,
    BookingSlugInspectionSessionListApiResponses
  >({
    getUrl: ({ slug, ...queryParams }) => apiUrl(`bookings/info-from-slug/${slug}/inspection-sessions/`, queryParams)
  }),
  getPaginatedInspectionSessions: dataEndpoint<InspectionSessionListApiData, InspectionSessionListApiResponses>({
    getUrl: (queryParams) => apiUrl('inspection-sessions/', queryParams)
  }),
  getInspectionSession: dataEndpoint<InspectionSessionGetApiData, InspectionSessionGetApiResponses>({
    getUrl: ({ sessionId }) => apiUrl(`inspection-sessions/${sessionId}/`)
  }),
  // Corrects one recorded observation rather than recording a second session.
  patchInspectionItem: responseEndpoint<InspectionItemPatchApiData, InspectionItemPatchApiResponses>({
    getUrl: ({ itemId }) => apiUrl(`inspection-items/${itemId}/`),
    getBody: ({ itemId: _, ...body }) => body
  }),
  // POST, not DELETE: the reason travels with it and a DELETE body is dropped.
  postInspectionSessionVoid: responseEndpoint<InspectionSessionVoidApiData, InspectionSessionVoidApiResponses>({
    getUrl: ({ sessionId }) => apiUrl(`inspection-sessions/${sessionId}/void/`),
    getBody: ({ sessionId: _, ...body }) => body
  })
};
